// @flow

import React, { useEffect, useRef, useState } from 'react'
import './GroupFilter.css'

import search from '../Icons/icon-activity-search.svg'
import close from '../Icons/icon-close-grey.svg'
import add from '../Icons/icon-activity-created.svg'

type Props = {
  changeFilter: Function,
  currentFilter: Function,
  groups: Array,
  profiles: Array
}

function GroupFilter ({ changeFilter, currentFilter, groups, profiles }: Props) {
  const groupDisplayRef = useRef()
  const searchRef = useRef()

  const profileFilter = currentFilter && Array.isArray(currentFilter.profiles) ? currentFilter.profiles : []
  const groupFilter = currentFilter && Array.isArray(currentFilter.groups) ? currentFilter.groups : []
  const [selected, setSelected] = useState(groupFilter)

  const [extended, setExtended] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [itemsToShow, setItemsToShow] = useState(10)

  const groupMap = new Map(groups.map((g) => { return [g._id, g] }))

  let groupArray = []
  profiles.forEach((profile) => {
    if (profileFilter.length === 0 || profileFilter.indexOf(profile._id) > -1) {
      groupArray = [...new Set([].concat(...[groupArray, profile.groups]))]
    }
  })
  if (!groupArray[0]) {
    groupArray = []
  }

  if (profileFilter.length === 0) {
    groupMap.forEach((v, k) => {
      if (!groupArray.includes(k)) {
        groupArray.push(k)
      }
    })
  }

  const groupData = groupArray.map((g) => groupMap.get(g) || { _id: false, name: 'Loading' })
  const groupList = new Map(groupData.map((g) => { return [g._id, g] }))

  const initialAddedGroups = selected.map((id, index) => {
    return groupList.get(id)
  })
  const [addedGroups, setAddedGroups] = useState(initialAddedGroups)
  const addedGroupsMap = new Map(addedGroups.map((g) => { return [g._id, g] }))

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleClick = (id) => {
    if (Array.isArray(id)) {
      setSelected([])
      setAddedGroups([])
      setExtended(false)
      setItemsToShow(10)
      groupDisplayRef.current.style.display = 'none'
      changeFilter(Object.assign({}, currentFilter, { groups: id }))
    } else {
      if (selected.indexOf(id) >= 0) {
        const copyArray = Array.from(selected)
        copyArray.splice(selected.indexOf(id), 1)
        setSelected(copyArray)

        const copyAddedGroups = Array.from(addedGroups)
        copyAddedGroups.splice(selected.indexOf(id), 1)
        setAddedGroups(copyAddedGroups)
        setItemsToShow(itemsToShow - 1)
        changeFilter(Object.assign({}, currentFilter, { groups: copyArray }))
      } else {
        setSelected([...selected, id])
        setAddedGroups([...addedGroups, groupList.get(id)])
        changeFilter(Object.assign({}, currentFilter, { groups: [...selected, id] }))
      }
    }
  }

  const searchChange = (e) => {
    setSearchTerm(e.currentTarget.value.split(' '))
  }

  const clearSearch = () => {
    setSearchTerm('')
    searchRef.current.value = ''
  }

  const handleGroupListClick = (e, id) => {
    handleClick(id)
    setItemsToShow(10)
    e.currentTarget.style.display = 'none'
  }

  const getData = () => {
    const list = []

    let searchPattern = ''
    if (searchTerm.length) {
      searchPattern = new RegExp(searchTerm.map(term => `(?=.*${term})`).join(''), 'i')
    }
    list.push(<li key='' onClick={() => handleClick([])}>All Groups <img className='AddIcon' src={add} alt='expand' /></li>)

    const data = groupData.filter((instance) => {
      if (searchPattern !== '') {
        if (!instance.name.match(searchPattern)) { return false }
      }
      if (addedGroupsMap.get(instance._id)) { return false }
      return instance._id
    }).map((instance) => {
      return <li key={instance._id} onClick={(e) => handleGroupListClick(e, instance._id)}>{instance.name} <img className='AddIcon' src={add} alt='expand' /></li>
    })

    return list.concat(data)
  }

  const getAddedGroups = () => {
    return addedGroups.map((group, index) => {
      return <span key={group._id} onClick={() => handleClick(group._id)}>{group.name} <img className='RemoveAddedGroupIcon' src={close} alt='expand' /></span>
    })
  }

  const handleClickOutside = (e) => {
    if (!groupDisplayRef.current.contains(e.target)) {
      setExtended(false)
      groupDisplayRef.current.style.display = 'none'
    }
  }

  const handleSearchClick = () => {
    groupDisplayRef.current.style.display = 'block'
    setExtended(true)
  }

  const handleSeeMore = (showMore) => {
    const items = showMore ? addedGroupsData.length : 10
    setItemsToShow(items)
  }

  const data = getData()
  const addedGroupsData = getAddedGroups()

  return (
    <div className='Header GroupHeader'>
      <div className='FilterByRow'>
        <div className='FilterByColumn'>
          <div>
            <span className='HeaderText'>Filter by Groups</span> <span onClick={() => handleClick([])}>Clear Filter</span>
          </div>
          <div className='GroupFilterContent'>
            <img className='SearchIcon' src={search} alt='expand' />
            <img className='CloseIcon' onClick={clearSearch} src={close} alt='expand' />
            <input ref={searchRef} onClick={handleSearchClick} onChange={searchChange} type='text' placeholder='Search Group Name' />
            <ul ref={groupDisplayRef} className='GroupDisplay'>{extended ? data : ''}</ul>
          </div>
        </div>
        <div className='FilterByColumn' style={{ flex: 2 }}>
          <div className='AddedGroups'>
            {addedGroupsData.slice(0, itemsToShow)}
            {(itemsToShow === 10 && addedGroupsData.length > 10) && (
              <span className='SeeMoreButton' onClick={() => handleSeeMore(true)}>See More...</span>
            )}
            {(itemsToShow > 10 && itemsToShow === addedGroupsData.length) && (
              <span className='SeeMoreButton' onClick={() => handleSeeMore(false)}>See Less...</span>
            )}
          </div>
        </div>
      </div>
      <hr />
    </div>
  )
}

export default GroupFilter
